"use client"

import React from "react"
import { handleErrorBoundary } from "@/lib/error-reporter"

interface HydrationErrorBoundaryState {
  hasError: boolean
  error?: Error
}

interface HydrationErrorBoundaryProps {
  children: React.ReactNode
  fallback?: React.ComponentType<{ error?: Error; retry: () => void }>
}

/**
 * Error Boundary specifically for catching hydration mismatches
 * Shows a user-friendly message instead of breaking the entire app
 */
export class HydrationErrorBoundary extends React.Component<
  HydrationErrorBoundaryProps,
  HydrationErrorBoundaryState
> {
  constructor(props: HydrationErrorBoundaryProps) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error: Error): HydrationErrorBoundaryState {
    // Check if this is a hydration error
    const isHydrationError = 
      error.message.includes('Hydration failed') ||
      error.message.includes('Text content did not match') ||
      error.message.includes('server rendered HTML') ||
      error.stack?.includes('throwOnHydrationMismatch')

    if (isHydrationError) {
      console.warn('🚨 [HYDRATION ERROR] Caught hydration mismatch:', {
        message: error.message,
        stack: error.stack,
        timestamp: new Date().toISOString(),
        userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown'
      })
    }

    return {
      hasError: !!isHydrationError,
      error: isHydrationError ? error : undefined
    }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // Log additional error info for hydration errors
    if (this.state.hasError) {
      console.warn('🚨 [HYDRATION ERROR] Component stack:', errorInfo.componentStack)
      // Report to server
      handleErrorBoundary(error, { componentStack: errorInfo.componentStack || '' })
    }
  }

  retry = () => {
    this.setState({ hasError: false, error: undefined })
  }

  render() {
    if (this.state.hasError) {
      const FallbackComponent = this.props.fallback || DefaultHydrationFallback
      return <FallbackComponent error={this.state.error} retry={this.retry} />
    }

    return this.props.children
  }
}

/**
 * Default fallback component for hydration errors
 * Shows a minimal message and retry button
 */
function DefaultHydrationFallback({ retry }: { error?: Error; retry: () => void }) {
  return (
    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
      <div className="flex items-center">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className="text-sm text-yellow-800">
            The page had a rendering issue, possibly due to a browser extension.
          </p>
          <button
            onClick={retry}
            className="mt-2 text-sm text-yellow-800 underline hover:text-yellow-900"
          >
            Try again
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Hook to catch hydration errors in functional components
 * Use this for components that frequently have hydration issues
 */
export function useHydrationErrorHandler() {
  React.useEffect(() => {
    const handleError = (event: ErrorEvent) => {
      const isHydrationError = 
        event.error?.message?.includes('Hydration failed') ||
        event.error?.message?.includes('Text content did not match')

      if (isHydrationError) {
        console.warn('🚨 [HYDRATION ERROR] Unhandled hydration error:', {
          message: event.error.message,
          filename: event.filename,
          lineno: event.lineno,
          colno: event.colno,
          timestamp: new Date().toISOString()
        })
        
        // Prevent the error from bubbling up and breaking the app
        event.preventDefault()
        return false
      }
    }

    window.addEventListener('error', handleError)
    return () => window.removeEventListener('error', handleError)
  }, [])
}